import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @mui/material
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Soft UI & Custom
import SoftBox from "Common/hooks/StyleFeatures/SoftBox";
import Breadcrumbs from "../../Breadcrumbs";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarMobileMenu,
} from "./styles";

import { useSoftUIController, setTransparentNavbar, setMiniSidenav } from "Common/hooks/context";
import useLearnerDashboard from "PlatForms/OrgLMS/Hooks/LearnerHooks/useLearnerDashboard";
import useManagerDashboard from "PlatForms/OrgLMS/Hooks/ManagerHooks/useManagerDashboard";
import useMentorDashboard from "PlatForms/OrgLMS/Hooks/MentorHooks/useMentorDashboard";
import { baseImageUrl } from "Common/Components/baseImageUrl";
import useAdminDashboard from "PlatForms/OrgLMS/Hooks/AdminHooks/useAdminDashboard";
import useSelfDashboard from "PlatForms/SelfLMS/Hooks/SelfLearner/useSelfDashboard";
import useExpertDashboard from "PlatForms/Dashboards/ExpertDashboard/Hooks/useExpertDashboard";

function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  const role = localStorage.getItem("role");

  // 👇 Only call relevant hook based on role
  const adminData = role === "Admin" ? useAdminDashboard().dashboardData : null;
  const learnerData = role === "Learner" ? useLearnerDashboard().dashboardData : null;
  const managerData = role === "Manager" ? useManagerDashboard().dashboardData : null;
  const mentorData = role === "Mentor" ? useMentorDashboard().dashboardData : null;
  const selfLearnerData = role === "SelfLearner" ? useSelfDashboard().dashboardData : null;
  const expertData = role === "Expert" ? useExpertDashboard().dashboardData : null;


  // Get the right data based on role
  const dashboardData =
    role === "Manager"
      ? managerData
      : role === "Mentor"
      ? mentorData
      : role === "Learner"
      ? learnerData
      : role === "SelfLearner"
      ? selfLearnerData
      : role === "Expert"
      ? expertData
      : adminData;

  // Avatar dropdown states
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const getRoleBasedData = (role, data) => {
    switch (role) {
      case "Manager":
        return {
          image: data?.manager?.profileImage,
          profileUrl: "/manager-profile",
        };
      case "Mentor":
        return {
          image: data?.mentor?.profileImage,
          profileUrl: "/mentor-profile",
        };
      case "Learner":
        return {
          image: data?.learner?.profileImage,
          profileUrl: "/learner-profile",
        };
      case "SelfLearner":
        return {
          // Fixed: Use learner instead of selflearner
          image: data?.learner?.profileImage,
          profileUrl: "/selfLearner-profile",
        };
         case "Expert":
        return {
          // Fixed: Use learner instead of selflearner
          image: data?.expert?.profileImage,
          profileUrl: "/expert-profile",
        };
      case "Admin":
        // No Admin profile page exists yet (component/hook/service and the
        // backend endpoint are all unbuilt), so no Profile link.
        return {
          image: data?.admin?.profileImage,
        };
      default:
        return {
          image: data?.learner?.profileImage,
        };
    }
  };

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    const handleTransparentNavbar = () => {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    };

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  const handleLogout = () => {
    localStorage.clear();
    window.dispatchEvent(new Event("storage"));
    navigate("/login");
  };

  const handlePersonalInterest = () => {
    navigate("/personal-interest");
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { image, profileUrl } = getRoleBasedData(role, dashboardData);
  console.log("dashboard data in index", dashboardData);
  console.log("Image URL:", image ? `${baseImageUrl}${image}` : "No image");

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox
          style={{ marginRight: "-20px" }}
          color="inherit"
          sx={(theme) => navbarRow(theme, { isMini })}
        >
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </SoftBox>

        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            <SoftBox color={light ? "white" : "inherit"}>
              {/* Mini sidenav toggle only for allowed roles */}
              {(role === "Manager" ||
                role === "Mentor" ||
                role === "Learner" ||
                role === "SelfLearner" ||
                role === "Expert" ||
                role === "SuperAdmin" ||
                role === "Admin") && (
                <IconButton color="inherit" sx={navbarMobileMenu} onClick={handleMiniSidenav}>
                  <Icon className={light ? "text-white" : "text-dark"}>
                    {miniSidenav ? "menu_open" : "menu"}
                  </Icon>
                </IconButton>
              )}
              <>
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                >
                  {image ? (
                    <Avatar
                      // Fixed: Ensure the image URL is properly constructed
                      src={image.startsWith('http') ? image : `${baseImageUrl}${image}`}
                      alt="profile"
                      sx={{ width: 40, height: 40 }}
                      onError={(e) => {
                        console.error("Image failed to load:", e.target.src);
                        e.target.onerror = null; // Prevent infinite loop
                        e.target.src = ""; // Clear the src to show the fallback
                      }}
                    />
                  ) : (
                    <Avatar sx={{ width: 40, height: 40 }} />
                  )}
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  id="account-menu"
                  open={open}
                  onClose={handleClose}
                  onClick={handleClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      mt: 1.5,
                      overflow: "visible",
                      filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                      "& .MuiAvatar-root": {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      "&:before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  {role !== "SuperAdmin" && profileUrl && (
                    <MenuItem onClick={() => navigate(profileUrl)}>Profile</MenuItem>
                  )}
                  {role === "SelfLearner" && (
                    <MenuItem onClick={handlePersonalInterest}>Personal Interest</MenuItem>
                  )}
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;